/**
 * The keys defined here are used to identify the queries in the database.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */

import {
  DEFAULT_CONVERSATIONS_LIMIT,
  type CorpusDocumentQuery,
  type DeletionPreviewRequest,
  type FetchConversationsParams,
} from "@noa/api-core";

export const AppConfigsQueryKey = {
  all: ["appConfigs"] as const,
} as const;

export const ConversationQueryKey = {
  all: ["conversations"] as const,
  lists: ["conversations", "list"] as const,
  list: (params: FetchConversationsParams = {}) => {
    const { limit = DEFAULT_CONVERSATIONS_LIMIT, offset = 0, filter = "", deleted = false, sort = "new-old" } = params;
    return [...ConversationQueryKey.lists, limit, offset, filter, deleted, sort] as const;
  },
  details: () => [...ConversationQueryKey.all, "details"] as const,
  detail: (sessionId: string) => [...ConversationQueryKey.details(), sessionId] as const,
} as const;

export const MessageQueryKey = {
  all: ["message"] as const,
  sessionMessagesList: (sessionId: string) => [...MessageQueryKey.all, "sessionMessagesList", sessionId] as const,
  sessionMessageDetail: (sessionId: string, messageId: string) =>
    [...MessageQueryKey.sessionMessagesList(sessionId), messageId] as const,
} as const;

export const LexiconQueryKey = {
  all: ["lexicon"] as const,
  subjects: (lang?: "en" | "fr") => [...LexiconQueryKey.all, "subjects", lang ?? "all"] as const,
  conceptsBySubject: (subject: string) => [...LexiconQueryKey.all, "concepts", subject] as const,
} as const;

export const DocumentQueryKey = {
  signedUrl: (blobPath: string) => ["documents", "signedUrl", blobPath] as const,
} as const;

export const SchemaQueryKey = {
  all: ["schemas"] as const,
  list: () => [...SchemaQueryKey.all, "list"] as const,
  detail: (schemaId: string) => [...SchemaQueryKey.all, "detail", schemaId] as const,
} as const;

export const UploadQueryKey = {
  all: ["uploads"] as const,
  list: () => [...UploadQueryKey.all, "list"] as const,
  job: (jobId: string) => [...UploadQueryKey.all, "job", jobId] as const,
  googleDriveFolders: () => [...UploadQueryKey.all, "googleDriveFolders"] as const,
  connectors: () => [...UploadQueryKey.all, "connectors"] as const,
} as const;

export const CorpusQueryKey = {
  all: ["corpus"] as const,
  access: () => [...CorpusQueryKey.all, "access"] as const,
  overview: () => [...CorpusQueryKey.all, "overview"] as const,
  values: (documentType: string, field: string) => [...CorpusQueryKey.all, "values", documentType, field] as const,
  documents: (query: CorpusDocumentQuery) => [...CorpusQueryKey.all, "documents", query] as const,
  detail: (sourceUrl: string, chunkOffset: number, chunkLimit: number) =>
    [...CorpusQueryKey.all, "detail", sourceUrl, chunkOffset, chunkLimit] as const,
  preview: (request: DeletionPreviewRequest | null) => [...CorpusQueryKey.all, "preview", request] as const,
} as const;
